// @ts-strict-ignore
import {StackableComponent, Visibility, VisibilityEvents} from 'common/components/base/stackable';
import {stringifyAttrsIfPojo} from 'common/util/jsx';

export interface ModalConfig<T> {
  /** tag name of modal like custom element  e.g mp-confirm */
  tagName: string;
  /** attributes to pass to the custom element modal when opened */
  attrs: any;
  /** properties to pass to the custom element modal when opened */
  props?: any;
  /** params to pass to the custom element modal when opened */
  params?: any;
  /** name of events that will close the modal and resolve the promise */
  closeEvents: string[];
  /** function handler to receive the custom event, the promise resolves with this return unless null */
  closeHandler?: (event: CustomEvent) => T | null;
  /**
   * This can be used to use promisifyModal with a react component. Inside the
   * caller must make sure to use React.createRoot to render the react component.
   * They will also be passed in a callback function that can close the modal
   * since events can not be bound/listened to easily with React components.
   */
  onElementCreatedCallback?: (modal: StackableComponent, closeModalCallback: () => void) => void;
  /**
   * This is a flag to determine if the modal should delay closing when the close event is triggered.
   */
  shouldDelayClose?: boolean;
}

// These events are known to be "cancellation" events that will result in the closure of the modal regardless
//  of the `open` attribute we can control. This occurs when a user clicks outside a modal or presses escape.
const knownClosureEvents = [VisibilityEvents[Visibility.Closed]];

// Magic tag that will be used to create the modal when using a react component
export const REACT_MODAL_TAG_NAME = `div`;

/**
 * This function will wrap any modal with a promise to allow for the invocation through js/ts instead of your template with open/close attributes
 * @param   modalConfig Config object to contain attributes, close events list
 * @returns Promise resolved with the custom event that triggered the closure
 */
export function promisifyModal<T = CustomEvent>({
  tagName,
  attrs = {},
  props = {},
  params = {},
  closeEvents,
  closeHandler,
  onElementCreatedCallback,
  shouldDelayClose = true,
}: ModalConfig<T>): Promise<T> {
  return new Promise((resolve) => {
    const modal = document.createElement(tagName) as StackableComponent;
    const eventsToListen = [...closeEvents, ...knownClosureEvents];
    const root =
      document.querySelector(`#width_wrapper > #modals`) ||
      document.querySelector(`mp-browser-context-root, mp-test-context-root`) ||
      document.body;

    const closeModal = () => {
      modal.setAttribute(`open`, `false`);

      if (shouldDelayClose) {
        setTimeout(() => {
          if (Array.prototype.find.call(root.childNodes, (n) => n === modal)) {
            root.removeChild(modal);
          }
        }, 500);
      } else {
        if (Array.prototype.find.call(root.childNodes, (n) => n === modal)) {
          root.removeChild(modal);
        }
      }
    };

    const closeEventCallback = (ev) => {
      const isModalClosureEvent = knownClosureEvents.includes(ev.type);
      const resolveValue = closeHandler ? closeHandler(ev) : ev;

      /**
       * TODO(pablo): promisifyModal is not using `mp-modals` as a root element, it is acting more like a
       * util function to place elements in document.body, a better implementation of this would be to always
       * host `mp-modal` and place something inside, however this requires updating all consumers of promisifyModal
       */
      const modalEl = [REACT_MODAL_TAG_NAME, `mp-modal`].includes(modal.tagName.toLowerCase())
        ? modal
        : modal.el.querySelector<StackableComponent>(`mp-modal:first-of-type`);

      // modelEl could be null in testcases when we are doing cleanupDOM
      const willClose = isModalClosureEvent ? modalEl?.stackId === ev.detail.target : resolveValue !== null;

      if (willClose) {
        resolve(resolveValue);
        closeModal();
        eventsToListen.forEach((eventName: string) => modal.removeEventListener(eventName, closeEventCallback));
      }
    };

    Object.entries(stringifyAttrsIfPojo(attrs)).forEach(([attr, val]) => modal.setAttribute(attr, val));
    Object.entries(props).forEach(([prop, val]) => (modal[prop] = val));

    if (params) {
      modal.setParams?.(params);
    }
    eventsToListen.forEach((eventName: string) => modal.addEventListener(eventName, closeEventCallback));

    root.appendChild(modal);
    onElementCreatedCallback?.(modal, () => closeModal());
    modal.setAttribute(`open`, `true`);
  });
}
